/**
 * Cloudflare Workers KV cache adapter
 *
 * This adapter allows using Cloudflare Workers KV as a distributed cache
 * backend for the TEFAS API client. Perfect for edge computing scenarios
 * where you want to cache API responses globally.
 *
 * @example
 * ```typescript
 * // In a Cloudflare Worker
 * import { TefasClient, CloudflareKVCacheAdapter } from '@firstthumb/tefas-api';
 *
 * export default {
 *   async fetch(request: Request, env: Env): Promise<Response> {
 *     const cache = new CloudflareKVCacheAdapter({
 *       namespace: env.TEFAS_CACHE,
 *       ttl: 15 * 60 * 1000
 *     });
 *
 *     const client = new TefasClient({ cacheAdapter: cache });
 *     const data = await client.getFund('2024-01-01', '2024-12-31');
 *
 *     return Response.json(data);
 *   }
 * };
 * ```
 */

import { CacheAdapter } from '../cache-interface';

/**
 * Minimal KV namespace interface
 *
 * This matches the Cloudflare Workers KV API without requiring
 * @cloudflare/workers-types as a dependency. If you have the types
 * package, they're fully compatible.
 */
export interface KVNamespace {
  get(key: string, options?: { type?: 'text' | 'json' | 'arrayBuffer' | 'stream' }): Promise<any>;
  put(key: string, value: string | ArrayBuffer | ReadableStream, options?: { expirationTtl?: number; expiration?: number }): Promise<void>;
  delete(key: string): Promise<void>;
  list(options?: { prefix?: string; limit?: number; cursor?: string }): Promise<{ keys: Array<{ name: string }>; list_complete: boolean; cursor?: string }>;
}

/**
 * Configuration options for Cloudflare KV cache adapter
 */
export interface CloudflareKVCacheOptions {
  /**
   * The Cloudflare KV namespace to use for caching
   *
   * This is typically bound to your Worker via wrangler.toml:
   * ```toml
   * [[kv_namespaces]]
   * binding = "TEFAS_CACHE"
   * id = "your-kv-namespace-id"
   * ```
   */
  namespace: KVNamespace;

  /**
   * Default time-to-live in milliseconds
   *
   * Note: This will be converted to seconds for KV storage.
   * Cloudflare KV minimum TTL is 60 seconds.
   *
   * @default 900000 (15 minutes)
   */
  ttl?: number;

  /**
   * Optional key prefix for namespace isolation
   *
   * Useful if sharing a KV namespace across multiple applications
   * or environments.
   *
   * @default "tefas:"
   * @example "prod:tefas:" or "staging:tefas:"
   */
  prefix?: string;
}

/**
 * Cloudflare Workers KV cache adapter
 *
 * Implements the CacheAdapter interface using Cloudflare Workers KV
 * as the storage backend. Values are automatically serialized to JSON.
 *
 * **Features:**
 * - Globally distributed edge caching
 * - Automatic JSON serialization
 * - TTL support (converted from milliseconds to seconds)
 * - Key prefixing for namespace isolation
 *
 * **Limitations:**
 * - `clear()` is not fully supported (KV has no bulk delete by pattern)
 * - `has()` performs a full `get()` operation (KV has no exists check)
 * - Eventual consistency (updates may take time to propagate globally)
 *
 * @template T - Type of values stored in the cache
 */
export class CloudflareKVCacheAdapter<T> implements CacheAdapter<T> {
  private readonly kv: KVNamespace;
  private readonly ttl: number;
  private readonly prefix: string;

  /**
   * Creates a new Cloudflare KV cache adapter
   *
   * @param options - Configuration options including KV namespace
   */
  constructor(options: CloudflareKVCacheOptions) {
    if (!options.namespace) {
      throw new Error('CloudflareKVCacheAdapter requires a KV namespace');
    }

    this.kv = options.namespace;
    this.ttl = options.ttl ?? 900000; // 15 minutes in milliseconds
    this.prefix = options.prefix ?? 'tefas:';
  }

  /**
   * Generates the full key with prefix
   *
   * @param key - Cache key
   * @returns Prefixed key
   */
  private getKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  /**
   * Converts TTL from milliseconds to seconds for KV
   *
   * Cloudflare KV requires TTL in seconds and has a minimum of 60 seconds.
   *
   * @param ttl - TTL in milliseconds
   * @returns TTL in seconds, minimum 60
   */
  private convertTtl(ttl: number): number {
    const seconds = Math.floor(ttl / 1000);
    return Math.max(seconds, 60); // KV minimum TTL is 60 seconds
  }

  /**
   * Retrieves a value from Cloudflare KV
   *
   * @param key - Cache key
   * @returns Cached value or undefined if not found or expired
   */
  async get(key: string): Promise<T | undefined> {
    try {
      const value = await this.kv.get(this.getKey(key), { type: 'json' });
      return value as T | undefined;
    } catch (error) {
      console.error(`CloudflareKVCacheAdapter.get() error for key "${key}":`, error);
      return undefined;
    }
  }

  /**
   * Stores a value in Cloudflare KV
   *
   * Values are automatically serialized to JSON. TTL is converted from
   * milliseconds to seconds with a minimum of 60 seconds.
   *
   * @param key - Cache key
   * @param value - Value to cache
   * @param ttl - Optional time-to-live in milliseconds (overrides default)
   */
  async set(key: string, value: T, ttl?: number): Promise<void> {
    try {
      const expirationTtl = this.convertTtl(ttl ?? this.ttl);
      const jsonValue = JSON.stringify(value);

      await this.kv.put(this.getKey(key), jsonValue, {
        expirationTtl
      });
    } catch (error) {
      console.error(`CloudflareKVCacheAdapter.set() error for key "${key}":`, error);
      throw error;
    }
  }

  /**
   * Removes a specific entry from Cloudflare KV
   *
   * @param key - Cache key to delete
   * @returns true (KV delete doesn't return a boolean, always returns true)
   */
  async delete(key: string): Promise<boolean> {
    try {
      await this.kv.delete(this.getKey(key));
      return true;
    } catch (error) {
      console.error(`CloudflareKVCacheAdapter.delete() error for key "${key}":`, error);
      return false;
    }
  }

  /**
   * Clears all cache entries with the configured prefix
   *
   * **⚠️ Limitation:** Cloudflare KV doesn't support efficient bulk deletion.
   * This method lists all keys with the prefix and deletes them individually,
   * which can be slow and may hit rate limits for large caches.
   *
   * For production use, consider these alternatives:
   * 1. Use key versioning (e.g., prefix with "v1:", "v2:")
   * 2. Let entries expire naturally via TTL
   * 3. Manually track keys in a separate list
   *
   * @throws Error if listing or deletion fails
   */
  async clear(): Promise<void> {
    console.warn(
      'CloudflareKVCacheAdapter.clear() is not optimized for KV. ' +
      'Consider using key versioning or TTL expiration instead.'
    );

    try {
      // List all keys with our prefix
      let cursor: string | undefined;
      const keysToDelete: string[] = [];

      do {
        const result = await this.kv.list({
          prefix: this.prefix,
          limit: 1000,
          cursor
        });

        keysToDelete.push(...result.keys.map((k) => k.name));
        cursor = result.list_complete ? undefined : result.cursor;
      } while (cursor);

      // Delete all keys (in batches to avoid rate limits)
      const deletePromises = keysToDelete.map((key) => this.kv.delete(key));
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('CloudflareKVCacheAdapter.clear() error:', error);
      throw error;
    }
  }

  /**
   * Checks if a key exists in Cloudflare KV
   *
   * **Note:** KV doesn't have a dedicated exists check, so this performs
   * a full `get()` operation. The value is not returned to save memory.
   *
   * @param key - Cache key
   * @returns true if key exists and is valid
   */
  async has(key: string): Promise<boolean> {
    try {
      const value = await this.kv.get(this.getKey(key), { type: 'text' });
      return value !== null;
    } catch (error) {
      console.error(`CloudflareKVCacheAdapter.has() error for key "${key}":`, error);
      return false;
    }
  }
}
